import { NavElementKey, ThemeConfig, ThemeNavStyle, ThemeTokenMap } from "@/lib/types";
import { defaultNavConfig, defaultThemeConfig } from "@/lib/theme";

// ── Пресеты-кандидаты тем для страницы сравнения /theme-compare ─────────────
// Один кандидат = одна запись. Процесс: референс (напр. с refero.design) →
// извлекаем палитру/радиус/типографику → пресет сюда; если у кандидата другая
// «форма» (стекло/тени/бордеры) — правила под [data-skin="<name>"] в app/skins.css.
// Победивший пресет промоутится в portal_data.json + lib/theme.ts +
// backend/app/core/default_data.py, его скин — в globals.css без скоупа.
// Кандидат может задавать тему ДВУМЯ способами:
//   config — полная тема (кандидат «с нуля», как раньше);
//   patch  — накладка НА ЖИВУЮ тему портала (меняем только то, что назвали).
// Накладка появилась 26.08 после граблей: PLUX задумывался как «другая тёмная
// палитра», а подменял тему целиком — и вместе с ней уезжали светлый режим,
// размер шрифта (в деве 12, в дефолте 15) и логотипы. Правило простое: меняем
// цвет — берём patch; собираем тему с нуля — config.
export type ThemePatch = {
  light?: Partial<ThemeTokenMap>;
  dark?: Partial<ThemeTokenMap>;
  radius?: number;
  baseFontSize?: number;
  /** По элементу — только те поля, что меняем (обычно один radius). */
  nav?: { [K in NavElementKey]?: Partial<ThemeNavStyle> };
};

export type ThemePreset = {
  /** slug: попадает в ?themePreview=, window.name и [data-skin] */
  name: string;
  label: string;
  config?: ThemeConfig;
  patch?: ThemePatch;
};

/** Тема, которую видит превью: полная из config либо живая с наложенным patch. */
export function resolvePreset(theme: ThemeConfig, preset: ThemePreset): ThemeConfig {
  if (preset.config) return preset.config;
  const p = preset.patch;
  if (!p) return theme;
  const baseNav = theme.nav ?? defaultNavConfig;
  return {
    ...theme,
    light: { ...theme.light, ...p.light },
    dark: { ...theme.dark, ...p.dark },
    radius: p.radius ?? theme.radius,
    baseFontSize: p.baseFontSize ?? theme.baseFontSize,
    nav: p.nav
      ? {
          top: { ...baseNav.top, ...p.nav.top },
          side: { ...baseNav.side, ...p.nav.side },
          aside: { ...baseNav.aside, ...p.nav.aside },
        }
      : theme.nav,
  };
}

// ⚠️ Кандидаты сравнения — ТОЛЬКО DEV. В production-сборке флаг = false, реестр
// пуст → на прод они не попадают (даже при ручном ?themePreview= — page-гейты
// тоже проверяют этот флаг). Так код может жить на main, но прод чист.
// process.env.NODE_ENV статически инлайнится Next: `next dev` = development,
// `next build` = production. yuc.konmak.ru (dev-контейнер) — их видит.
export const PREVIEW_CANDIDATES_ENABLED = process.env.NODE_ENV !== "production";

const PREVIEW_CANDIDATES: ThemePreset[] = [
  // Победивший вариант ГЛАВНОЙ — «Dala» (частицы-морф). Рендерит app/home/page.tsx
  // по previewName; путь «Дашборд» (/home) в theme-compare. Остальные кандидаты
  // (Студия/Вход-*/Шёлк/Мягкий градиент/Спотлайт/Синтвейв/Свисс/Аврора-стекло)
  // отклонены и убраны из списка примерки.
  { name: "home-dala", label: "Главная: Dala (частицы-морф 🦄)", config: defaultThemeConfig },
];

// На проде — пустой реестр (кандидаты только для dev-сравнилки).
export const themePresets: ThemePreset[] = PREVIEW_CANDIDATES_ENABLED ? PREVIEW_CANDIDATES : [];

export function getThemePreset(name: string | null | undefined): ThemePreset | null {
  if (!name) return null;
  return themePresets.find((preset) => preset.name === name) ?? null;
}

// ── Флаг превью ──────────────────────────────────────────────────────────────
// Приоритет: ?themePreview=<name> в URL (так пейн/вкладку открывает
// /theme-compare), затем window.name — он у каждого iframe/вкладки СВОЙ и
// переживает полные перезагрузки, поэтому кандидатская тема не слетает даже
// при hard reload внутри пейна (SPA-переходы её и так не трогают).
const PREVIEW_WINDOW_NAME_PREFIX = "yuc-theme-preview:";
export const PREVIEW_QUERY_PARAM = "themePreview";

export function readThemePreviewFlag(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(PREVIEW_QUERY_PARAM);
    if (fromUrl) return fromUrl;
    if (window.name.startsWith(PREVIEW_WINDOW_NAME_PREFIX)) {
      return window.name.slice(PREVIEW_WINDOW_NAME_PREFIX.length) || null;
    }
  } catch {
    /* window.name недоступен — некритично */
  }
  return null;
}

export function writeThemePreviewFlag(name: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (name) {
      window.name = PREVIEW_WINDOW_NAME_PREFIX + name;
    } else if (window.name.startsWith(PREVIEW_WINDOW_NAME_PREFIX)) {
      window.name = "";
    }
  } catch {
    /* noop */
  }
}
